import { take, takeEvery, getContext, fork, call } from 'redux-saga/effects';
import type Analytics from '@simplywallst/analytics';
import { logout } from '@/redux/auth/routines';
import { QUERY_KEY } from '@/hooks/useUser';
import type { AnyAction } from 'redux';
import type { ProfileState } from '@/hooks/useUser';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Watches all saga actions and calls handleIdentify
 * If handleIdentify returns true we stop watching
 */
function* identifyWatcher(analytics: Analytics) {
  while (true) {
    try {
      const action = yield take('*');
      const result = yield call(handleIdentify, analytics, action);
      if (result) {
        break;
      }
    } catch (e) {}
  }
}

/**
 * Forked saga that will ignore the action and search the state for the user's id
 * If the user id exists and we're not identified dispatch the `Identify` event
 */
function* handleIdentify(analytics: Analytics, _action: AnyAction) {
  try {
    const queryClient: QueryClient = yield getContext('queryClient');
    const userData = queryClient.getQueryData<ProfileState>(QUERY_KEY);

    if (!analytics.isIdentified() && userData?.id) {
      analytics.dispatch({ type: 'identify', id: userData.id });
      analytics.dispatch({
        type: 'track',
        page: 'login',
        subject: 'success',
        action: 'identify',
        userId: userData.id,
      });
      return true;
    }
  } catch (e) {
    return false;
  }
}

/**
 * Capture logout actions and clear the identified flag in the analytics
 * Also fork a new instance of identifyWatcher incase they log back in
 */
function* handleLogout(analytics: Analytics, _action: AnyAction) {
  analytics.logout();
  yield fork(identifyWatcher, analytics);
}

function* watcher() {
  try {
    const analytics = yield getContext('analytics');
    if (!analytics) {
      return;
    }
    yield takeEvery(logout.SUCCESS, handleLogout, analytics);
    yield fork(identifyWatcher, analytics);
  } catch (e) {}
}

export const watchers = [watcher];
